import * as XLSX from 'xlsx';
import { format } from 'date-fns';

interface ContractYearRow {
  monthYear: string;
  postProdStatNetgen: number | null;
}

interface ReportData {
  vendorName: string | null;
  energySource: string | null;
  ippType: string | null;
  meterNumber: string | null;
  location: string | null;
  contractStart: string | null;
  contractEnd: string | null;
  rows: ContractYearRow[];
}

// Given a start month number (1-12), group sorted month_year strings into contract years.
const groupByContractYear = (
  rows: ContractYearRow[],
  contractStartMonth: number
): Map<number, ContractYearRow[]> => {
  const map = new Map<number, ContractYearRow[]>();
  for (const row of rows) {
    const [yearStr, monthStr] = row.monthYear.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const contractYear = month >= contractStartMonth ? year : year - 1;
    if (!map.has(contractYear)) map.set(contractYear, []);
    map.get(contractYear)!.push(row);
  }
  return map;
};


// Parse "YYYY-MM-DD" or "YYYY-MM" to a comparable "YYYY-MM" key.
const toMonthKey = (dateStr: string): string => dateStr.substring(0, 7);

const contractTotalHeader = (contractStart: string | null, contractEnd: string | null): string => {
  const fmt = (s: string | null) => {
    if (!s) return 'N/A';
    const [year, month] = s.split('-');
    return `${Number(month)}-${year}`;
  };
  return `Contract Total\n${fmt(contractStart)} to ${fmt(contractEnd)}`;
};

export const exportContractYearReport = (data: ReportData) => {
  const contractStartMonth = data.contractStart
    ? Number(data.contractStart.substring(5, 7))
    : 1;

  const wb = XLSX.utils.book_new();

  const groupedYears = groupByContractYear(data.rows, contractStartMonth);
  const sortedYears = Array.from(groupedYears.keys()).sort((a, b) => a - b);

  if (!sortedYears.length) return;

  // Build positional month headers from contractStartMonth (e.g. Apr, May, ..., Mar)
  const monthHeaders: string[] = [];
  for (let m = 0; m < 12; m++) {
    let mo = contractStartMonth + m;
    if (mo > 12) mo -= 12;
    const d = new Date(2000, mo - 1, 1);
    monthHeaders.push(format(d, 'MMM'));
  }

  // Contract Total bounds as "YYYY-MM" keys for comparison
  const contractStartKey = data.contractStart ? toMonthKey(data.contractStart) : null;
  const contractEndKey = data.contractEnd ? toMonthKey(data.contractEnd) : null;

  const contractTotalCol = contractTotalHeader(data.contractStart, data.contractEnd);

  // Sheet header rows
  const sheetRows: (string | number | null)[][] = [
    [data.vendorName ?? ''],
    [`${data.ippType ?? ''} - ${data.meterNumber ?? ''}`],
    [data.location ?? ''],
    [],
    ['Contract Year', ...monthHeaders, 'Running Total', contractTotalCol],
  ];

  for (const year of sortedYears) {
    const yearRows = groupedYears.get(year)!.sort((a, b) => a.monthYear.localeCompare(b.monthYear));
    const valueMap = new Map(yearRows.map(r => [r.monthYear, r.postProdStatNetgen]));

    // Compute this year's own month keys by position (offset 0-11 from contractStartMonth)
    const yearMonthKeys: string[] = [];
    for (let m = 0; m < 12; m++) {
      let y = year, mo = contractStartMonth + m;
      if (mo > 12) { mo -= 12; y += 1; }
      yearMonthKeys.push(`${y}-${String(mo).padStart(2, '0')}`);
    }

    const cells: (number | null)[] = yearMonthKeys.map(key => valueMap.get(key) ?? null);

    const runningTotal = cells.reduce<number>((sum, v) => sum + (v ?? 0), 0);

    // Contract Total: sum only months within contractStart–contractEnd
    const contractTotal = yearMonthKeys.reduce<number>((sum, key, i) => {
      const inRange =
        (!contractStartKey || key >= contractStartKey) &&
        (!contractEndKey || key <= contractEndKey);
      return inRange ? sum + (cells[i] ?? 0) : sum;
    }, 0);

    const endYear = year + 1;
    const yearLabel = `${year}-${String(endYear).slice(-2)}`;

    sheetRows.push([yearLabel, ...cells, runningTotal, contractTotal]);
  }

  const ws = XLSX.utils.aoa_to_sheet(sheetRows);

  ws['!cols'] = [
    { wch: 14 },
    ...monthHeaders.map(() => ({ wch: 10 })),
    { wch: 14 },
    { wch: 20 },
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Contract Year Totals');

  const now = new Date();
  const asOf = format(now, 'MM_yy');
  const safeName = (s: string | null) => (s ?? '').replaceAll(/[^a-zA-Z0-9_\-. ]/g, '_').trim();
  const filename = `${safeName(data.meterNumber)} ${safeName(data.location)} Running Contract Year totals Report as of ${asOf}.xlsx`;

  XLSX.writeFile(wb, filename);
};
